import { IContextualMenuItem } from "@fluentui/react";
import React from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../src/taskpane/redux";
import { LanguageContext } from "../../../utilities/LocalizationModule";
import { customToast } from "../../../src/taskpane/components/CustomToast";
import { isTemplateDatasourceLocation } from "../../../src/taskpane/components/helpers/customFieldsHelper";
import { getFolderPermissionMenuState, runFolderPermissionMenuAction } from "./folderPermissionMenuState";
import { CM_BODY_TOAST_CONTAINER_ID } from "./useMenuItem_ScanCustomFields";
import { menuItemIconStyles } from "../../../src/taskpane/config/controlStyles";
import { FolderSearch20Regular } from "../../../src/taskpane/components/_v2/components/SvgIcons/FolderSearch20Regular";
import { isSupportedTemplateExtension, suggestPlaceholdersExtensions } from "../../../src/taskpane/config/constants";

const bodyToast = { containerId: CM_BODY_TOAST_CONTAINER_ID };

export interface ISuggestPlaceholdersTarget {
    templateUniqueId: string;
    templateFileName: string;
    siteUrl: string;
    listId: string;
    /** The grid row this panel was opened for. Captured HERE rather than read from the
     *  selection at close time: the panel outlives any number of selection changes, and the
     *  host refreshes this row after the panel reports. */
    gridItem: any;
}

/**
 * "Suggest placeholders (AI)" — a sibling of the Scan command with the same location and
 * permission gating PLUS `templateAiEnabled`: template text leaves the infrastructure when a
 * suggestion runs, so the item simply does not render unless it may. Requires exactly ONE
 * selected template file.
 *
 * `templateAiEnabled` carries BOTH gates already, combined server-side (see ITenantInfo): the
 * per-tenant opt-in AND the server's TemplateAi configuration. Do not treat it as the tenant
 * flag alone — with the section disabled or missing its Endpoint/ApiKey no orchestrator is
 * registered at all, and rendering the command would open a panel whose first call is
 * refused. The client cannot see server configuration, which is exactly why the server
 * combines them (the IsTessEnabled precedent).
 */
export function useMenuItem_SuggestPlaceholders(
    selectedItems: any[],
    waitFor: boolean,
    onOpenPanel: (target: ISuggestPlaceholdersTarget) => void,
) {
    const languageStrings = React.useContext(LanguageContext);

    const datasources = useSelector((state: RootState) => state.tenantInfo?.datasources);
    const templateAiEnabled = useSelector((state: RootState) => state.tenantInfo?.templateAiEnabled === true);
    const currentSharepointFolder = useSelector((state: RootState) => state.currentSharepointFolder);
    const currentDirectory = useSelector((state: RootState) => state.workingDirectories.workingDirectories.at(-1));

    const menuItem_SuggestPlaceholders = React.useMemo(() => {
        if (!templateAiEnabled) return null;

        const templateDatasource = isTemplateDatasourceLocation(
            datasources,
            currentDirectory?.webUrl,
            currentDirectory?.DocumentLibrary ?? currentDirectory?.sharepointList);
        if (!templateDatasource) return null;

        const permissionState = getFolderPermissionMenuState({
            currentSharepointFolder,
            requiredPermissions: ["canEditListItem"],
        });
        if (!permissionState.shouldRender) return null;

        const selectedFiles = (selectedItems ?? [])
            .filter(item => item?.FSObjType !== "1" && item?.FileSystemObjectType !== 1);

        // Hidden for a file the AI path cannot open at all. Only decidable once the selection
        // names ONE file - with none or several selected the command still renders and the
        // click explains itself (SuggestPlaceholdersSelectOne), because there is no single
        // extension to judge.
        //
        // The server refuses these anyway ("'.odt' templates are not supported by AI
        // suggestions"), but only after the panel has opened, previewed and round-tripped -
        // so without this the user pays a panel, a download and a parse to be told no.
        if (selectedFiles.length === 1
            && !isSupportedTemplateExtension(
                selectedFiles[0]?.FileLeafRef ?? selectedFiles[0]?.name ?? "",
                suggestPlaceholdersExtensions)) {
            return null;
        }

        return {
            key: "suggestPlaceholders",
            // webUrl too: an undefined siteUrl would reach the server as a 400 the panel
            // can only render as a generic failure.
            disabled: permissionState.isDisabled || waitFor || !currentDirectory?.sharePointListId || !currentDirectory?.webUrl,
            iconProps: {},
            onRenderIcon: () => <FolderSearch20Regular className={menuItemIconStyles} />,
            text: languageStrings.SuggestPlaceholders,
            onClick: () => {
                runFolderPermissionMenuAction(permissionState, () => {
                    if (selectedFiles.length !== 1) {
                        customToast.info(languageStrings.SuggestPlaceholdersSelectOne, bodyToast);
                        return;
                    }
                    const item = selectedFiles[0];
                    onOpenPanel({
                        templateUniqueId: (item?.UniqueId ?? item?.uniqueid ?? "").replace("{", "").replace("}", ""),
                        templateFileName: item?.FileLeafRef ?? item?.name ?? "",
                        siteUrl: currentDirectory?.webUrl,
                        listId: currentDirectory?.sharePointListId,
                        gridItem: item,
                    });
                });
            },
        } as IContextualMenuItem;
    }, [templateAiEnabled, datasources, currentDirectory, currentSharepointFolder, selectedItems, waitFor, languageStrings, onOpenPanel]);

    return { menuItem_SuggestPlaceholders };
}
